/**
 * BulkPasteSelector Component
 *
 * Allows users to paste a list of email addresses or wallet addresses
 * for bulk selection of airdrop recipients.
 *
 * @component
 */

import React, { useState } from "react";
import { parseBulkInput, matchWalletsFromInput } from "../utils";
import { colors, gradients, commonStyles } from "../styles";
import { PrimaryButton, OutlineButton, AccentButton } from "./Button";

const BulkPasteSelector = ({
  wallets,
  onAddWallets,
  selectedCount,
  maxRecipients,
  inputText,
  setInputText,
  parseResult,
  setParseResult,
}) => {
  const [addedFeedback, setAddedFeedback] = useState(null);

  const handleParse = () => {
    const identifiers = parseBulkInput(inputText);
    if (identifiers.length === 0) {
      setParseResult({
        matched: [],
        unmatched: [],
        error: "No valid entries found",
      });
      return;
    }

    const result = matchWalletsFromInput(identifiers, wallets);
    setParseResult(result);
    setAddedFeedback(null);
  };

  const handleAddMatched = () => {
    if (parseResult?.matched?.length > 0) {
      const availableSlots = maxRecipients - selectedCount;
      const walletsToAdd = parseResult.matched.slice(0, availableSlots);
      onAddWallets(walletsToAdd);
      setAddedFeedback(walletsToAdd.length);
      setTimeout(() => setAddedFeedback(null), 3000);
    }
  };

  const handleClear = () => {
    setInputText("");
    setParseResult(null);
  };

  return (
    <div style={{ padding: "20px" }}>
      {/* Input Section */}
      <div style={{ marginBottom: "16px" }}>
        <label style={commonStyles.label}>
          Paste Email Addresses or Wallet Addresses
        </label>
        <p
          style={{
            margin: "0 0 12px 0",
            fontSize: "13px",
            color: colors.gray600,
            lineHeight: "1.5",
          }}
        >
          Enter one email or wallet address per line, or separate them with
          commas. Only addresses that match existing Hedera wallets will be
          added.
        </p>
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder={`user1@example.com\nuser2@example.com\n0.0.123456\n\nOr paste comma-separated:\nuser1@example.com, user2@example.com, 0.0.123456`}
          style={{
            width: "100%",
            minHeight: "180px",
            padding: "14px",
            border: `1px solid ${colors.gray400}`,
            borderRadius: "4px",
            fontSize: "14px",
            fontFamily: "monospace",
            lineHeight: "1.6",
            resize: "vertical",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
        <PrimaryButton onClick={handleParse} disabled={!inputText.trim()}>
          Parse & Validate
        </PrimaryButton>
        <OutlineButton onClick={handleClear}>Clear</OutlineButton>
      </div>

      {/* Parse Results */}
      {parseResult && (
        <div
          style={{
            background: colors.gray100,
            border: `1px solid ${colors.border}`,
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          {/* Results Header */}
          <div
            style={{
              padding: "16px 20px",
              borderBottom: `1px solid ${colors.border}`,
              background: colors.white,
            }}
          >
            <h4
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                color: colors.primary,
              }}
            >
              Validation Results
            </h4>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              {/* Matched Count */}
              <div
                style={{
                  padding: "12px 20px",
                  background:
                    parseResult.matched.length > 0
                      ? gradients.success
                      : colors.gray300,
                  borderRadius: "4px",
                  textAlign: "center",
                  minWidth: "120px",
                }}
              >
                <span
                  style={{
                    display: "block",
                    fontSize: "24px",
                    fontWeight: 700,
                    color: colors.white,
                  }}
                >
                  {parseResult.matched.length}
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.9)",
                    fontWeight: 600,
                  }}
                >
                  Matched
                </span>
              </div>
              {/* Unmatched Count */}
              <div
                style={{
                  padding: "12px 20px",
                  background:
                    parseResult.unmatched.length > 0
                      ? gradients.danger
                      : colors.gray300,
                  borderRadius: "4px",
                  textAlign: "center",
                  minWidth: "120px",
                }}
              >
                <span
                  style={{
                    display: "block",
                    fontSize: "24px",
                    fontWeight: 700,
                    color: colors.white,
                  }}
                >
                  {parseResult.unmatched.length}
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.9)",
                    fontWeight: 600,
                  }}
                >
                  Not Found
                </span>
              </div>
            </div>
          </div>

          {/* Matched Wallets Preview */}
          {parseResult.matched.length > 0 && (
            <div
              style={{
                padding: "16px 20px",
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <h5
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "13px",
                  color: colors.success,
                }}
              >
                ✓ Matched Wallets ({parseResult.matched.length})
              </h5>
              <div
                style={{
                  maxHeight: "120px",
                  overflowY: "auto",
                  background: colors.white,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "4px",
                  padding: "8px",
                }}
              >
                {parseResult.matched.slice(0, 20).map((wallet, idx) => (
                  <div
                    key={wallet.address}
                    style={{
                      padding: "6px 10px",
                      fontSize: "13px",
                      borderBottom:
                        idx < parseResult.matched.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span style={{ color: colors.primary }}>
                      {wallet.email || wallet.address}
                    </span>
                    <code style={{ fontSize: "11px", color: colors.gray600 }}>
                      {wallet.address}
                    </code>
                  </div>
                ))}
                {parseResult.matched.length > 20 && (
                  <div
                    style={{
                      padding: "6px 10px",
                      fontSize: "12px",
                      color: colors.gray600,
                      fontStyle: "italic",
                    }}
                  >
                    ... and {parseResult.matched.length - 20} more
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Unmatched Entries */}
          {parseResult.unmatched.length > 0 && (
            <div
              style={{
                padding: "16px 20px",
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <h5
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "13px",
                  color: colors.danger,
                }}
              >
                ✗ Not Found ({parseResult.unmatched.length})
              </h5>
              <div
                style={{
                  maxHeight: "80px",
                  overflowY: "auto",
                  background: "#fff5f5",
                  border: "1px solid #f5c6cb",
                  borderRadius: "4px",
                  padding: "8px 12px",
                  fontSize: "12px",
                  color: "#721c24",
                  fontFamily: "monospace",
                }}
              >
                {parseResult.unmatched.slice(0, 10).join(", ")}
                {parseResult.unmatched.length > 10 &&
                  ` ... and ${parseResult.unmatched.length - 10} more`}
              </div>
            </div>
          )}

          {/* Add Button */}
          {parseResult.matched.length > 0 && (
            <div style={{ padding: "16px 20px", background: colors.white }}>
              <AccentButton
                onClick={handleAddMatched}
                disabled={addedFeedback !== null}
                style={{
                  background:
                    addedFeedback !== null
                      ? gradients.success
                      : gradients.accent,
                }}
              >
                {addedFeedback !== null ? (
                  <>
                    <span style={{ marginRight: "8px" }}>✓</span>
                    {addedFeedback} Recipients Added to Selection!
                  </>
                ) : (
                  <>Add {parseResult.matched.length} Recipients to Selection</>
                )}
              </AccentButton>
              {selectedCount + parseResult.matched.length > maxRecipients && (
                <p
                  style={{
                    margin: "10px 0 0",
                    fontSize: "12px",
                    color: colors.accent,
                  }}
                >
                  ⚠ Only {maxRecipients - selectedCount} slots available. First{" "}
                  {maxRecipients - selectedCount} will be added.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkPasteSelector;
